import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Flame, Crown, Trophy, User as UserIcon } from "lucide-react";
import StreakBadges from "../components/StreakBadges";

export default function Profile() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/profile/me").then((res) => setData(res.data)).catch(() => {});
  }, []);

  if (!user) return null;
  const total = user.wins + user.losses + user.ties;
  const winRate = total > 0 ? Math.round((user.wins / total) * 100) : 0;

  return (
    <div className="grid-bg min-h-[calc(100vh-4rem)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <div className="neo-flat glow-cyan p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-2xl bg-amber-400/10 border border-amber-400/60 flex items-center justify-center">
              <UserIcon className="w-8 h-8 text-amber-300" />
            </div>
            <div className="flex-1">
              <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-slate-50" data-testid="profile-username">
                {user.username}
              </h1>
              <div className="text-sm text-slate-400" data-testid="profile-email">
                {user.is_guest ? "Guest Player (not on leaderboard)" : user.email}
              </div>
              <div className="mt-3" data-testid="profile-streak-badges">
                <StreakBadges current={user.current_streak || 0} best={user.best_streak || 0} size="lg" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Current Streak" value={user.current_streak} icon={<Flame className="w-5 h-5 text-orange-400" />} testId="stat-current-streak" glow="glow-amber" />
          <Stat label="Best Streak" value={user.best_streak} icon={<Crown className="w-5 h-5 text-amber-300" />} testId="stat-best-streak" glow="glow-amber" />
          <Stat label="Wins" value={user.wins} icon={<Trophy className="w-5 h-5 text-emerald-300" />} testId="stat-wins" glow="glow-lime" />
          <Stat label="Win Rate" value={`${winRate}%`} icon={<UserIcon className="w-5 h-5 text-violet-300" />} testId="stat-winrate" glow="glow-violet" />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Stat label="Wins" value={user.wins} testId="stat-wins-detail" glow="glow-lime" />
          <Stat label="Losses" value={user.losses} testId="stat-losses" glow="glow-red" />
          <Stat label="Ties" value={user.ties} testId="stat-ties" />
        </div>

        <div className="neo-flat p-6 sm:p-8">
          <h2 className="font-display font-bold text-2xl mb-4 text-slate-100">Match History</h2>
          {!data || data.history.length === 0 ? (
            <div className="text-slate-500 py-6 text-center" data-testid="history-empty">No matches yet. Head to the arena!</div>
          ) : (
            <ul className="space-y-3" data-testid="history-list">
              {data.history.map((m) => {
                const idx = m.players.indexOf(user.id);
                const yourKey = idx === 0 ? "p1" : "p2";
                const oppName = m.player_usernames[1 - idx];
                let outcome = "tie";
                if (m.winner === yourKey) outcome = "win";
                else if (m.winner !== "tie") outcome = "loss";
                return (
                  <li key={m.id} className="flex items-center justify-between p-3 border border-slate-700 rounded-xl bg-slate-900/60">
                    <div>
                      <div className="font-semibold text-slate-100">
                        vs {oppName} {m.is_bot && <span className="text-xs text-violet-300">(bot)</span>}
                      </div>
                      <div className="text-xs font-mono-stat text-slate-500">
                        {new Date(m.finished_at).toLocaleString()} • BO{m.best_of}
                      </div>
                    </div>
                    <div className={`chip ${outcome === "win" ? "chip-green" : outcome === "loss" ? "chip-red" : "chip-slate"}`}>
                      {outcome.toUpperCase()} {m.final_score.p1}–{m.final_score.p2}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon, testId, glow = "" }) {
  return (
    <div className={`neo-flat p-4 ${glow}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="font-mono-stat text-[10px] uppercase text-slate-400 tracking-widest">{label}</div>
        {icon}
      </div>
      <div className="font-display font-black text-3xl sm:text-4xl mt-1 text-slate-50">{value}</div>
    </div>
  );
}
